import { gql, ApolloClient, HttpLink, InMemoryCache, ApolloLink } from "@apollo/client";
import { onError } from "apollo-link-error";
import { forceLogout } from "../Axios/authInterceptor";
import { maziwa_baseurl } from "../../config";

// Switch between local dev and the configured environment by toggling the line below:
//const WALLET_GQL_URL = "https://localhost:7100/graphql/";
const WALLET_GQL_URL = `${maziwa_baseurl}/wallet`;

// ─── Auth middleware ────────────────────────────────────────────────────────
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token.replace(/"/g, "")}` } : {};
};

const authMiddleware = new ApolloLink((operation, forward) => {
  const headers = getAuthHeaders();
  if (headers.Authorization) {
    operation.setContext({ headers });
  }
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message }) =>
      console.error(`[Wallet GQL] ${message}`)
    );
  }
  if (networkError && networkError.statusCode === 401) {
    forceLogout();
  }
});

// ─── Wallet Apollo Client factory ───────────────────────────────────────────
export const createWalletClient = () => {
  const httpLink = new HttpLink({ uri: WALLET_GQL_URL });
  return new ApolloClient({
    link: errorLink.concat(authMiddleware.concat(httpLink)),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only" },
      query: { fetchPolicy: "network-only" },
    },
  });
};

// =============================================================================
// QUERIES  (match WalletQueryResolver + WalletQueryResolverV2)
// =============================================================================

// ── Platform credentials ─────────────────────────────────────────────────────

/**
 * Fetch the Sababisha Global Paybill shortcode from dbo.mpesa_credentials
 * (company_id = 27). Returns null when no row exists — callers should fall
 * back to the architectural constant "400200".
 */
export const SABABISHA_SHORT_CODE_QUERY = gql`
  query GetSababishaShortCode {
    sababishaShortCode
  }
`;

// ── V1 Core ──────────────────────────────────────────────────────────────────

/** Fetch the company wallet summary — CompanyWallet model */
export const WALLET_QUERY = gql`
  query Wallet($companyId: Int) {
    wallet(companyId: $companyId) {
      id
      companyId
      availableBalance
      escrowedBalance
      minimumRetainedBalance
      lifecycleState
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

/** Fetch the overdraft facility for a company — OverdraftFacility model */
export const OVERDRAFT_FACILITY_QUERY = gql`
  query OverdraftFacility($companyId: Int) {
    overdraftFacility(companyId: $companyId) {
      id
      companyId
      facilityLimit
      outstandingPrincipal
      accruedInterest
      annualInterestRatePct
      investorSharePct
      status
      investorReference
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

/** List all overdraft facilities (optionally filtered by company) */
export const OVERDRAFT_FACILITIES_QUERY = gql`
  query GetOverdraftFacilities($companyId: Int) {
    overdraftFacilities(companyId: $companyId) {
      id
      companyId
      facilityLimit
      outstandingPrincipal
      accruedInterest
      annualInterestRatePct
      investorSharePct
      status
      investorReference
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

/** List maker-checker overdraft facility requests (optionally filtered by company / status) */
export const OVERDRAFT_FACILITY_REQUESTS_QUERY = gql`
  query GetOverdraftFacilityRequests($companyId: Int, $status: String) {
    overdraftFacilityRequests(companyId: $companyId, status: $status) {
      id
      companyId
      requestedLimit
      annualInterestRatePct
      investorSharePct
      investorReference
      requestedBy
      requestedDate
      approvedBy
      approvedDate
      rejectedBy
      rejectionReason
      status
      facilityId
      notes
      createdDate
    }
  }
`;

// ── Disbursement Fee Schedule (V1) ───────────────────────────────────────────

/** List all disbursement fee schedules */
export const FEE_SCHEDULES_QUERY = gql`
  query FeeSchedules {
    feeSchedules {
      id
      name
      status
      effectiveFrom
      retiredFrom
      notes
      createdBy
      createdDate
      approvedBy
      approvedDate
    }
  }
`;

/** Get a specific disbursement fee schedule by ID */
export const FEE_SCHEDULE_QUERY = gql`
  query FeeSchedule($scheduleId: Int!) {
    feeSchedule(scheduleId: $scheduleId) {
      id
      name
      status
      effectiveFrom
      retiredFrom
      notes
      createdBy
      createdDate
      approvedBy
      approvedDate
    }
  }
`;

/** Get the currently active disbursement fee schedule */
export const ACTIVE_FEE_SCHEDULE_QUERY = gql`
  query ActiveFeeSchedule {
    activeFeeSchedule {
      id
      name
      status
      effectiveFrom
      retiredFrom
      notes
      createdBy
      createdDate
      approvedBy
      approvedDate
    }
  }
`;

/** Get fee tier bands for a disbursement schedule (amountFrom / amountTo). */
export const FEE_TIERS_QUERY = gql`
  query FeeTiers($scheduleId: Int!) {
    feeTiers(scheduleId: $scheduleId) {
      id
      scheduleId
      amountFrom
      amountTo
      sababishaCharge
      mpesaCharge
      totalCharge
      createdBy
      createdDate
    }
  }
`;

/** Validate that disbursement fee bands are coherent and non-overlapping */
export const VALIDATE_FEE_SCHEDULE_BANDS_QUERY = gql`
  query ValidateFeeScheduleBands($scheduleId: Int!) {
    validateFeeScheduleBands(scheduleId: $scheduleId)
  }
`;

/**
 * Preview disbursement charge breakdown for a given amount.
 * BE: calculateCharges(amount: Decimal!) — WalletQueryResolver
 */
export const CALCULATE_CHARGES_QUERY = gql`
  query CalculateCharges($amount: Decimal!) {
    calculateCharges(amount: $amount) {
      sababishaCharge
      mpesaCharge
      totalCharge
      tierId
      scheduleId
    }
  }
`;

/**
 * Preview charges for a specific disbursement schedule (admin preview).
 * BE: calculateChargesForSchedule(scheduleId, amount) — WalletQueryResolver
 */
export const CALCULATE_CHARGES_FOR_SCHEDULE_QUERY = gql`
  query CalculateChargesForSchedule($scheduleId: Int!, $amount: Decimal!) {
    calculateChargesForSchedule(scheduleId: $scheduleId, amount: $amount) {
      sababishaCharge
      mpesaCharge
      totalCharge
      tierId
      scheduleId
    }
  }
`;

/**
 * Preview royalty on withdrawal amount.
 * BE: calculateWithdrawalRoyalty(withdrawalAmount: Decimal!) — WalletQueryResolver
 */
export const CALCULATE_WITHDRAWAL_ROYALTY_QUERY = gql`
  query CalculateWithdrawalRoyalty($withdrawalAmount: Decimal!) {
    calculateWithdrawalRoyalty(withdrawalAmount: $withdrawalAmount) {
      sababishaCharge
      mpesaCharge
      totalCharge
      tierId
      scheduleId
    }
  }
`;

// ── V2 — Wallet Ledger ────────────────────────────────────────────────────────

/**
 * Paginated wallet ledger entries for a company.
 * BE: getWalletLedger(companyId, page, pageSize) — WalletQueryResolverV2
 * Returns: WalletLedger[]
 */
export const WALLET_LEDGER_QUERY = gql`
  query GetWalletLedger($companyId: Int, $page: Int, $pageSize: Int) {
    walletLedger(companyId: $companyId, page: $page, pageSize: $pageSize) {
      id
      walletId
      companyId
      txType
      amount
      balanceAfter
      refType
      refId
      description
      glJournalId
      createdBy
      createdDate
      approvedBy
      approvedById
      recipient
      sababishaCharge
      mpesaCharge
    }
  }
`;
export const WALLET_ALL_LEDGER_QUERY = gql`
  query GetAllWalletLedger($page: Int, $pageSize: Int) {
    walletLedger(page: $page, pageSize: $pageSize) {
      id
      walletId
      companyId
      txType
      amount
      balanceAfter
      refType
      refId
      description
      glJournalId
      createdBy
      createdDate
      approvedBy
      approvedById
      recipient
      sababishaCharge
      mpesaCharge
    }
  }
`;
// ── V2 — Withdrawal Fee Schedule ──────────────────────────────────────────────

/**
 * List all withdrawal fee schedules.
 * BE: getWithdrawalFeeSchedules() — WalletQueryResolverV2
 * Returns: WithdrawalFeeSchedule[]
 */
export const WITHDRAWAL_FEE_SCHEDULES_QUERY = gql`
  query GetWithdrawalFeeSchedules {
    withdrawalFeeSchedules {
      id
      name
      status
      effectiveFrom
      retiredFrom
      notes
      createdBy
      createdDate
      approvedBy
      approvedDate
      updatedBy
    }
  }
`;

/**
 * Get the currently ACTIVE withdrawal fee schedule.
 * BE: getActiveWithdrawalFeeSchedule() — WalletQueryResolverV2
 * Returns: WithdrawalFeeSchedule?
 */
export const ACTIVE_WITHDRAWAL_FEE_SCHEDULE_QUERY = gql`
  query GetActiveWithdrawalFeeSchedule {
    activeWithdrawalFeeSchedule {
      id
      name
      status
      effectiveFrom
      retiredFrom
      notes
      createdBy
      createdDate
      approvedBy
      approvedDate
      updatedBy
    }
  }
`;

/**
 * Tiers for a given withdrawal fee schedule.
 * BE: getWithdrawalFeeTiers(scheduleId) — WalletQueryResolverV2
 * Returns: WithdrawalFeeTier[]  (amountFrom, amountTo, sababishaCharge, mpesaCharge, chargeType)
 */
export const WITHDRAWAL_FEE_TIERS_QUERY = gql`
  query GetWithdrawalFeeTiers($scheduleId: Int!) {
    withdrawalFeeTiers(scheduleId: $scheduleId) {
      id
      scheduleId
      amountFrom
      amountTo
      sababishaCharge
      mpesaCharge
      chargeType
      createdBy
      createdDate
    }
  }
`;

/**
 * Calculate withdrawal charges against the active withdrawal fee schedule.
 * BE: calculateWithdrawalCharges(amount: Decimal!) — WalletQueryResolverV2
 * Returns: WithdrawalChargeBreakdown
 */
export const CALCULATE_WITHDRAWAL_CHARGES_QUERY = gql`
  query CalculateWithdrawalCharges($amount: Decimal!) {
    calculateWithdrawalCharges(amount: $amount) {
      grossAmount
      sababishaCharge
      mpesaCharge
      totalCharges
      netAmount
      chargeType
      tierId
    }
  }
`;

// ── V2 — Royalty Engine ───────────────────────────────────────────────────────

/**
 * Returns the active royalty schedule for a company.
 * BE: getActiveRoyaltySchedule(companyId?) — WalletQueryResolverV2
 * Returns: RoyaltySchedule?
 */
export const ACTIVE_ROYALTY_SCHEDULE_QUERY = gql`
  query GetActiveRoyaltySchedule($companyId: Int) {
    activeRoyaltySchedule(companyId: $companyId) {
      id
      companyId
      royaltyPct
      periodType
      effectiveFrom
      effectiveTo
      revenueBasis
      contractReference
      status
      createdBy
      createdDate
      approvedBy
      approvedDate
      updatedBy
      updatedDate
    }
  }
`;

/**
 * Returns all royalty schedules for a company.
 * BE: getRoyaltySchedules(companyId?) — WalletQueryResolverV2
 * Returns: RoyaltySchedule[]
 */
export const ROYALTY_SCHEDULES_QUERY = gql`
  query GetRoyaltySchedules($companyId: Int) {
    royaltySchedules(companyId: $companyId) {
      id
      companyId
      royaltyPct
      periodType
      effectiveFrom
      effectiveTo
      revenueBasis
      contractReference
      status
      createdBy
      createdDate
      approvedBy
      approvedDate
      updatedBy
      updatedDate
    }
  }
`;

/**
 * Returns royalty accruals for a company, optionally filtered by date range.
 * BE: getRoyaltyAccruals(companyId?, from?, to?) — WalletQueryResolverV2
 * Returns: RoyaltyAccrual[]
 */
export const ROYALTY_ACCRUALS_QUERY = gql`
  query GetRoyaltyAccruals($companyId: Int, $from: DateTime, $to: DateTime) {
    royaltyAccruals(companyId: $companyId, from: $from, to: $to) {
      id
      companyId
      scheduleId
      periodStart
      periodEnd
      revenueAmount
      royaltyAmount
      status
      deductedFromWallet
      glJournalId
      createdBy
      createdDate
      deductedDate
      notes
    }
  }
`;

// ── V2 — Withdrawal Requests ──────────────────────────────────────────────────

/**
 * Returns a single withdrawal request by ID.
 * BE: getWithdrawalRequest(requestId: long) — WalletQueryResolverV2
 * Returns: WithdrawalRequest?
 */
export const WITHDRAWAL_REQUEST_QUERY = gql`
  query GetWithdrawalRequest($requestId: Long!) {
    withdrawalRequest(requestId: $requestId) {
      id
      companyId
      walletId
      amount
      withdrawalFee
      mpesaFee
      netAmount
      recipientPhone
      bankAccountNumber
      bankCode
      requestedBy
      requestedDate
      approvedBy
      approvedDate
      rejectedBy
      rejectedDate
      rejectionReason
      status
      otpVerified
      otpExpiresAt
      conversationId
      originatorConvId
      safaricomResultCode
      safaricomResultDesc
      mpesaReceipt
      notes
      glJournalId
      walletLedgerId
      createdDate
      updatedDate
    }
  }
`;

/**
 * Returns paginated withdrawal requests for a company.
 * Status filter: PENDING_APPROVAL | APPROVED | REJECTED | PROCESSING | COMPLETED | FAILED
 * BE: getWithdrawalRequests(companyId?, status?, page, pageSize) — WalletQueryResolverV2
 * Returns: WithdrawalRequest[]
 */
export const WITHDRAWAL_REQUESTS_QUERY = gql`
  query GetWithdrawalRequests(
    $companyId: Int
    $status: String
    $page: Int
    $pageSize: Int
  ) {
    withdrawalRequests(
      companyId: $companyId
      status: $status
      page: $page
      pageSize: $pageSize
    ) {
      id
      companyId
      walletId
      amount
      withdrawalFee
      mpesaFee
      netAmount
      recipientPhone
      bankAccountNumber
      bankCode
      requestedBy
      requestedDate
      approvedBy
      approvedDate
      rejectedBy
      rejectedDate
      rejectionReason
      status
      otpVerified
      otpExpiresAt
      conversationId
      originatorConvId
      safaricomResultCode
      safaricomResultDesc
      mpesaReceipt
      notes
      glJournalId
      walletLedgerId
      createdDate
      updatedDate
    }
  }
`;

// ── V2 — Overdraft Ledger ─────────────────────────────────────────────────────

/**
 * Paginated overdraft ledger entries for a company.
 * BE: getOverdraftLedger(companyId?, page, pageSize) — WalletQueryResolverV2
 * Returns: OverdraftLedger[]
 */
export const OVERDRAFT_LEDGER_QUERY = gql`
  query OverdraftLedger($companyId: Int, $page: Int, $pageSize: Int) {
    overdraftLedger(companyId: $companyId, page: $page, pageSize: $pageSize) {
      id
      facilityId
      companyId
      txType
      amount
      outstandingAfter
      disbursementId
      glJournalId
      description
      createdBy
      createdDate
    }
  }
`;

// ── V2 — General Ledger ───────────────────────────────────────────────────────

/**
 * Returns the full chart of accounts (active accounts only).
 * BE: getChartOfAccounts() — WalletQueryResolverV2
 * Returns: GlAccount[]
 */
export const CHART_OF_ACCOUNTS_QUERY = gql`
  query GetChartOfAccounts {
    chartOfAccounts {
      id
      code
      name
      accountType
      normalSide
      isActive
      description
      createdDate
    }
  }
`;

/**
 * Returns a single GL journal by ID, including lines.
 * BE: getGlJournal(journalId: long) — WalletQueryResolverV2
 * Returns: GlJournal?
 */
export const GL_JOURNAL_QUERY = gql`
  query GetGlJournal($journalId: Long!) {
    glJournal(journalId: $journalId) {
      id
      journalType
      companyId
      refType
      refId
      description
      postedBy
      postedDate
      isReversal
      reversedById
    }
  }
`;

/**
 * Returns paginated GL journals, optionally filtered by companyId or refType.
 * refType: DISBURSEMENT | WITHDRAWAL | TOP_UP | ROYALTY | etc.
 * BE: getGlJournals(companyId?, refType?, page, pageSize) — WalletQueryResolverV2
 * Returns: GlJournal[]
 */
export const GL_JOURNALS_QUERY = gql`
  query GetGlJournals(
    $companyId: Int
    $refType: String
    $page: Int
    $pageSize: Int
  ) {
    glJournals(
      companyId: $companyId
      refType: $refType
      page: $page
      pageSize: $pageSize
    ) {
      id
      journalType
      companyId
      refType
      refId
      description
      postedBy
      postedDate
      isReversal
      reversedById
    }
  }
`;

/**
 * Returns all debit and credit lines for a GL journal.
 * BE: getGlJournalLines(journalId: long) — WalletQueryResolverV2
 * Returns: GlJournalLine[]
 */
export const GL_JOURNAL_LINES_QUERY = gql`
  query GetGlJournalLines($journalId: Long!) {
    glJournalLines(journalId: $journalId) {
      id
      journalId
      accountId
      side
      amount
      description
      accountCode
      accountName
    }
  }
`;

// ── V2 — Deposit Escrow ───────────────────────────────────────────────────────

/**
 * Returns a deposit escrow record by its STK Push checkout ID.
 * BE: getDepositEscrow(checkoutId: string) — WalletQueryResolverV2
 * Returns: DepositEscrow?
 */
export const DEPOSIT_ESCROW_QUERY = gql`
  query GetDepositEscrow($checkoutId: String!) {
    depositEscrow(checkoutId: $checkoutId) {
      id
      companyId
      amount
      mpesaCheckoutId
      mpesaReceipt
      phoneNumber
      status
      paybillBalanceBefore
      paybillBalanceAfter
      balanceConfirmedAt
      walletCredited
      walletLedgerId
      glJournalId
      expiresAt
      createdBy
      createdDate
      updatedDate
    }
  }
`;

// =============================================================================
// MUTATIONS  (match WalletMutationResolver + WalletMutationResolverV2)
// =============================================================================

// ── V1 — Disbursement Fee Schedule lifecycle ──────────────────────────────────

/** Create a new (draft) disbursement fee schedule */
export const CREATE_FEE_SCHEDULE_MUTATION = gql`
  mutation CreateFeeSchedule($input: CreateFeeScheduleInput!) {
    createFeeSchedule(input: $input)
  }
`;

/** Upsert (create or update) disbursement fee tier bands for a schedule */
export const UPSERT_FEE_TIERS_MUTATION = gql`
  mutation UpsertFeeTiers($input: UpsertFeeTiersInput!) {
    upsertFeeTiers(input: $input)
  }
`;

/** Submit a disbursement fee schedule for approval (DRAFT → PENDING_APPROVAL) */
export const SUBMIT_FEE_SCHEDULE_FOR_APPROVAL_MUTATION = gql`
  mutation SubmitFeeScheduleForApproval($scheduleId: Int!) {
    submitFeeScheduleForApproval(scheduleId: $scheduleId)
  }
`;

/** Approve a pending disbursement fee schedule (maker ≠ checker enforced) */
export const APPROVE_FEE_SCHEDULE_MUTATION = gql`
  mutation ApproveFeeSchedule($scheduleId: Int!) {
    approveFeeSchedule(scheduleId: $scheduleId)
  }
`;

/** Retire an active disbursement fee schedule */
export const RETIRE_FEE_SCHEDULE_MUTATION = gql`
  mutation RetireFeeSchedule($scheduleId: Int!) {
    retireFeeSchedule(scheduleId: $scheduleId)
  }
`;

// ── V1 — Escrow / Wallet ops ──────────────────────────────────────────────────

/** Reserve (escrow) funds against the wallet for a pending disbursement */
export const ESCROW_FUNDS_MUTATION = gql`
  mutation EscrowFunds($input: EscrowFundsInput!) {
    escrowFunds(input: $input)
  }
`;

/** Commit (settle) an existing escrow after successful B2C callback */
export const COMMIT_ESCROW_MUTATION = gql`
  mutation CommitEscrow($disbursementId: Int!) {
    commitEscrow(disbursementId: $disbursementId)
  }
`;

/** Release (reverse) a locked escrow on disbursement failure */
export const RELEASE_ESCROW_MUTATION = gql`
  mutation ReleaseEscrow($disbursementId: Int!) {
    releaseEscrow(disbursementId: $disbursementId)
  }
`;

/** Top up the company wallet */
export const TOP_UP_WALLET_MUTATION = gql`
  mutation TopUpWallet($input: TopUpInput!) {
    topUpWallet(input: $input)
  }
`;

/** Direct withdrawal from wallet (V1) */
export const WITHDRAW_FROM_WALLET_MUTATION = gql`
  mutation WithdrawFromWallet($input: WithdrawInput!) {
    withdrawFromWallet(input: $input)
  }
`;

// ── V2 — Withdrawal Maker-Checker ─────────────────────────────────────────────

/**
 * MAKER step: creates a withdrawal request and generates an OTP.
 * BE: requestWithdrawal(RequestWithdrawalInput) → RequestWithdrawalPayload
 * Input fields: companyId, amount, recipientPhone?, bankAccountNumber?, bankCode?, notes?
 * Returns: { requestId, otpForAdmin }
 */
/**
 * MAKER step: requests an OTP to be sent to the requester's email before
 * submitting a withdrawal request.
 * BE: requestWithdrawalOtp(actionType: String) → FEOutput { results, error }
 */
export const REQUEST_WITHDRAWAL_OTP_MUTATION = gql`
  mutation RequestWithdrawalOtp($actionType: String) {
    requestWithdrawalOtp(actionType: $actionType) {
      results
      error
    }
  }
`;

/**
 * MAKER step: validates the 6-digit OTP before submitting the withdrawal request.
 * BE: validateWithdrawalOtp(otp: String!) → FEOutput { results, error }
 */
export const VALIDATE_WITHDRAWAL_OTP_MUTATION = gql`
  mutation ValidateWithdrawalOtp($otp: String!) {
    validateWithdrawalOtp(otp: $otp) {
      results
      error
    }
  }
`;

export const REQUEST_WITHDRAWAL_MUTATION = gql`
  mutation RequestWithdrawal($input: RequestWithdrawalInput!) {
    requestWithdrawal(input: $input) {
      requestId
      otpForAdmin
    }
  }
`;

/**
 * CHECKER step: verifies OTP, enforces maker ≠ checker, debits wallet, posts GL.
 * BE: approveWithdrawal(ApproveWithdrawalInput) → Int (HTTP status equivalent)
 * Input fields: requestId (Long), otpPlaintext (String)
 */
export const APPROVE_WITHDRAWAL_MUTATION = gql`
  mutation ApproveWithdrawal($input: ApproveWithdrawalInput!) {
    approveWithdrawal(input: $input)
  }
`;

/**
 * Rejects a pending withdrawal request.
 * BE: rejectWithdrawal(requestId: long, reason: string) → Int
 */
export const REJECT_WITHDRAWAL_MUTATION = gql`
  mutation RejectWithdrawal($requestId: Long!, $reason: String!) {
    rejectWithdrawal(requestId: $requestId, reason: $reason)
  }
`;

// ── V2 — Withdrawal Fee Schedule lifecycle ────────────────────────────────────

/**
 * Creates a new withdrawal fee schedule in DRAFT status.
 * BE: createWithdrawalFeeSchedule(CreateWithdrawalFeeScheduleInput) → Int (scheduleId)
 * Input: name (String!), effectiveFrom (DateTime?), notes (String?)
 */
export const CREATE_WITHDRAWAL_FEE_SCHEDULE_MUTATION = gql`
  mutation CreateWithdrawalFeeSchedule($input: CreateWithdrawalFeeScheduleInput!) {
    createWithdrawalFeeSchedule(input: $input)
  }
`;

/**
 * Upserts tiers for a DRAFT withdrawal fee schedule.
 * BE: upsertWithdrawalFeeTiers(UpsertWithdrawalFeeTiersInput) → Boolean
 * Input: scheduleId (Int!), tiers [{ amountFrom, amountTo, sababishaCharge, mpesaCharge, chargeType }]
 */
export const UPSERT_WITHDRAWAL_FEE_TIERS_MUTATION = gql`
  mutation UpsertWithdrawalFeeTiers($input: UpsertWithdrawalFeeTiersInput!) {
    upsertWithdrawalFeeTiers(input: $input)
  }
`;

/**
 * Submits a withdrawal fee schedule for approval (DRAFT → PENDING_APPROVAL).
 * BE: submitWithdrawalFeeScheduleForApproval(scheduleId: Int!) → Boolean
 */
export const SUBMIT_WITHDRAWAL_FEE_SCHEDULE_FOR_APPROVAL_MUTATION = gql`
  mutation SubmitWithdrawalFeeScheduleForApproval($scheduleId: Int!) {
    submitWithdrawalFeeScheduleForApproval(scheduleId: $scheduleId)
  }
`;

/**
 * Approves a withdrawal fee schedule (maker ≠ checker enforced).
 * BE: approveWithdrawalFeeSchedule(scheduleId: Int!) → Boolean
 */
export const APPROVE_WITHDRAWAL_FEE_SCHEDULE_MUTATION = gql`
  mutation ApproveWithdrawalFeeSchedule($scheduleId: Int!) {
    approveWithdrawalFeeSchedule(scheduleId: $scheduleId)
  }
`;

/**
 * Retires the currently ACTIVE withdrawal fee schedule.
 * BE: retireWithdrawalFeeSchedule(scheduleId: Int!) → Boolean
 */
export const RETIRE_WITHDRAWAL_FEE_SCHEDULE_MUTATION = gql`
  mutation RetireWithdrawalFeeSchedule($scheduleId: Int!) {
    retireWithdrawalFeeSchedule(scheduleId: $scheduleId)
  }
`;

// ── V2 — Royalty Engine ───────────────────────────────────────────────────────

/**
 * Creates a royalty schedule (DRAFT) for a company.
 * BE: createRoyaltySchedule(CreateRoyaltyScheduleInput) → Int (scheduleId)
 * Input: companyId, royaltyPct, periodType (MONTHLY|QUARTERLY|ANNUAL),
 *        effectiveFrom, effectiveTo?, revenueBasis (DISBURSEMENTS|COLLECTIONS|NET_INCOME),
 *        contractReference?
 */
export const CREATE_ROYALTY_SCHEDULE_MUTATION = gql`
  mutation CreateRoyaltySchedule($input: CreateRoyaltyScheduleInput!) {
    createRoyaltySchedule(input: $input)
  }
`;

/**
 * Approves a royalty schedule (maker ≠ checker enforced).
 * Supersedes any existing active royalty schedule for that company.
 * BE: approveRoyaltySchedule(scheduleId: Int!) → Int
 */
export const APPROVE_ROYALTY_SCHEDULE_MUTATION = gql`
  mutation ApproveRoyaltySchedule($scheduleId: Int!) {
    approveRoyaltySchedule(scheduleId: $scheduleId)
  }
`;

/**
 * Manually accrues royalty for a company over a period.
 * (In production this is the monthly batch job; this is the manual trigger.)
 * BE: accrueRoyalty(companyId, periodStart, periodEnd) → Long (accrualId or -1)
 */
export const ACCRUE_ROYALTY_MUTATION = gql`
  mutation AccrueRoyalty(
    $companyId: Int!
    $periodStart: DateTime!
    $periodEnd: DateTime!
  ) {
    accrueRoyalty(
      companyId: $companyId
      periodStart: $periodStart
      periodEnd: $periodEnd
    )
  }
`;

/**
 * Deducts an accrued royalty from the company wallet.
 * BE: deductRoyalty(accrualId: long) → Int
 */
export const DEDUCT_ROYALTY_MUTATION = gql`
  mutation DeductRoyalty($accrualId: Long!) {
    deductRoyalty(accrualId: $accrualId)
  }
`;

// ── V2 — Overdraft Facility CRUD ──────────────────────────────────────────────

/**
 * MAKER step: creates an overdraft facility request (must be approved by checker).
 * BE: requestOverdraftFacility(RequestOverdraftFacilityInput) → Long (requestId)
 * Input: companyId, requestedLimit, annualInterestRatePct, investorSharePct,
 *        investorReference?, notes?
 */
export const REQUEST_OVERDRAFT_FACILITY_MUTATION = gql`
  mutation RequestOverdraftFacility($input: RequestOverdraftFacilityInput!) {
    requestOverdraftFacility(input: $input)
  }
`;

/**
 * MAKER step: requests an OTP to be sent to the maker's email before they
 * submit an overdraft facility request.
 * BE: requestOverdraftMakerOtp() → FEOutput { results, error }
 */
export const REQUEST_OVERDRAFT_MAKER_OTP_MUTATION = gql`
  mutation RequestOverdraftMakerOtp {
    requestOverdraftMakerOtp {
      results
      error
    }
  }
`;

/**
 * MAKER step: validates the 6-digit OTP before submitting the overdraft
 * facility request. Does NOT consume the OTP (consumption happens on the
 * final requestOverdraftFacility call server-side).
 * BE: validateOverdraftMakerOtp(otp: String!) → FEOutput { results, error }
 */
export const VALIDATE_OVERDRAFT_MAKER_OTP_MUTATION = gql`
  mutation ValidateOverdraftMakerOtp($otp: String!) {
    validateOverdraftMakerOtp(otp: $otp) {
      results
      error
    }
  }
`;

/**
 * Requests an OTP to be sent to the approver's email before approving
 * a withdrawal or overdraft facility.
 * actionType: "WITHDRAWAL_APPROVAL" | "OVERDRAFT_APPROVAL"
 * entityId: the withdrawal ID or overdraft facility request ID
 * BE: requestApprovalOtp(actionType: String!, entityId: Long!) → Boolean
 */
export const REQUEST_APPROVAL_OTP_MUTATION = gql`
  mutation RequestApprovalOtp($actionType: String!, $entityId: Long!) {
    requestApprovalOtp(actionType: $actionType, entityId: $entityId)
  }
`;

/**
 * CHECKER step: approves an overdraft facility request (maker ≠ checker).
 * Creates or updates the overdraft_facility record.
 * otp is the code the approver received via email.
 * BE: approveOverdraftFacility(requestId: long, otp: String!) → Int
 */
export const APPROVE_OVERDRAFT_FACILITY_MUTATION = gql`
  mutation ApproveOverdraftFacility($requestId: Long!, $otp: String!) {
    approveOverdraftFacility(requestId: $requestId, otp: $otp)
  }
`;

/**
 * Suspends an active overdraft facility (no new draws until reactivated).
 * BE: suspendOverdraftFacility(companyId: Int!) → Int
 */
export const SUSPEND_OVERDRAFT_FACILITY_MUTATION = gql`
  mutation SuspendOverdraftFacility($companyId: Int!) {
    suspendOverdraftFacility(companyId: $companyId)
  }
`;

/**
 * Permanently closes an overdraft facility.
 * BE: closeOverdraftFacility(companyId: Int!) → Int
 */
export const CLOSE_OVERDRAFT_FACILITY_MUTATION = gql`
  mutation CloseOverdraftFacility($companyId: Int!) {
    closeOverdraftFacility(companyId: $companyId)
  }
`;

// =============================================================================
// SGW APPROVAL PERMISSIONS (Role Group ID 1 Superusers Governance)
// =============================================================================

/**
 * Fetch all SGW approver permission records from backend.
 * Filters out soft-deleted records by default unless includeInactive is true.
 */
export const GET_SGW_APPROVALS_QUERY = gql`
  query GetSgwApprovals($includeInactive: Boolean) {
    sgwApprovals(includeInactive: $includeInactive) {
      id
      userId
      name
      username
      email
      phone
      roleName
      roleGroupId
      assignedApproval
      assignedApprovalLabel
      isApproved
      effectiveDate
      assignedBy
      notes
      isDeleted
      createdAt
      updatedAt
    }
  }
`;

/**
 * Fetch all active superusers belonging to Role Group ID 1.
 */
export const GET_SGW_SUPERUSERS_QUERY = gql`
  query GetSgwSuperusersOnly {
    sgwSuperusers {
      id
      name
      username
      email
      phone
      roleId
      roleName
      roleGroupId
    }
  }
`;

/**
 * Assign one or more SGW approvals to a Superuser (Role Group ID 1).
 */
export const ASSIGN_SGW_APPROVALS_MUTATION = gql`
  mutation AssignSgwApprovals($input: AssignSgwApprovalsInput!) {
    assignSgwApprovals(input: $input) {
      id
      userId
      name
      username
      email
      phone
      roleName
      roleGroupId
      assignedApproval
      assignedApprovalLabel
      isApproved
      effectiveDate
      assignedBy
      notes
      isDeleted
    }
  }
`;

/**
 * Update an existing SGW approval permission (toggle active status or edit notes).
 */
export const UPDATE_SGW_APPROVAL_MUTATION = gql`
  mutation UpdateSgwApproval($input: UpdateSgwApprovalInput!) {
    updateSgwApproval(input: $input) {
      id
      userId
      assignedApproval
      isApproved
      notes
      updatedBy
      updatedAt
    }
  }
`;

/**
 * Soft delete an SGW approval permission.
 * Sets isDeleted = true, records deletedAt and deletedBy.
 */
export const SOFT_DELETE_SGW_APPROVAL_MUTATION = gql`
  mutation SoftDeleteSgwApproval($id: ID!, $deletedBy: String) {
    softDeleteSgwApproval(id: $id, deletedBy: $deletedBy)
  }
`;

// =============================================================================
// Helper wrappers
// =============================================================================

/**
 * Extracts the most useful error message from any Apollo / GraphQL error.
 *
 * Priority:
 *  1. GraphQL errors[].extensions.message  (HotChocolate BUSINESS_RULE)
 *  2. GraphQL errors[].message             (standard GraphQL error)
 *  3. NetworkError body errors[].message   (HTTP 4xx/5xx with GQL body)
 *  4. NetworkError.message                 (plain HTTP / network failure)
 *  5. Fallback generic string
 */
function extractGqlErrorMessage(err) {
  // Case A: graphQLErrors array (works when errorPolicy:'all' is set)
  if (err?.graphQLErrors?.length) {
    const e = err.graphQLErrors[0];
    return e?.extensions?.message || e?.message || null;
  }

  // Case B: networkError that still contains a GQL body (HTTP 500 + errors[])
  const netResult = err?.networkError?.result;
  if (netResult?.errors?.length) {
    const e = netResult.errors[0];
    return e?.extensions?.message || e?.message || null;
  }

  // Case C: plain network / server error
  if (err?.networkError?.message) return err.networkError.message;

  // Case D: ordinary Error
  if (err?.message) return err.message;

  return 'An unexpected error occurred.';
}

/**
 * Convenience wrapper — runs a GQL query against the wallet service.
 * Returns the `data` field on success, throws on error.
 */
export async function walletQuery(document, variables = {}) {
  try {
    const client = createWalletClient();
    const { data, errors } = await client.query({ query: document, variables });
    if (errors?.length) throw new Error(extractGqlErrorMessage({ graphQLErrors: errors }));
    return data;
  } catch (err) {
    throw new Error(extractGqlErrorMessage(err));
  }
}

/**
 * Convenience wrapper — runs a GQL mutation against the wallet service.
 * Returns the `data` field on success, throws on error.
 *
 * Uses `errorPolicy: 'all'` so that business-rule errors returned with
 * HTTP 200 are surfaced in `errors[]`. For HTTP 500 responses that still
 * carry a GQL error body, the catch block extracts the real message from
 * `networkError.result.errors[]`.
 */
export async function walletMutate(mutation, variables = {}) {
  try {
    const client = createWalletClient();
    const { data, errors } = await client.mutate({
      mutation,
      variables,
      errorPolicy: 'all',
    });
    if (errors?.length) throw new Error(extractGqlErrorMessage({ graphQLErrors: errors }));
    return data;
  } catch (err) {
    // Re-extract if this is an ApolloError thrown despite errorPolicy:'all'
    // (happens when the server returns HTTP 4xx/5xx)
    throw new Error(extractGqlErrorMessage(err));
  }
}

// ── SGW Approval Permissions Service Wrappers ────────────────────────────────

/**
 * Fetch SGW Approvals from the GraphQL API.
 */
export async function fetchSgwApprovalsFromApi(includeInactive = false) {
  try {
    const data = await walletQuery(GET_SGW_APPROVALS_QUERY, { includeInactive });
    return data?.sgwApprovals || [];
  } catch (err) {
    console.warn('[Wallet GQL] fetchSgwApprovalsFromApi:', err.message);
    throw err;
  }
}

/**
 * Fetch Superusers with Role Group ID 1 from the GraphQL API.
 */
export async function fetchSgwSuperusersFromApi() {
  try {
    const data = await walletQuery(GET_SGW_SUPERUSERS_QUERY);
    return data?.sgwSuperusers || [];
  } catch (err) {
    console.warn('[Wallet GQL] fetchSgwSuperusersFromApi:', err.message);
    throw err;
  }
}

/**
 * Assign SGW Approvals to a superuser via API.
 */
export async function assignSgwApprovalsApi(input) {
  return walletMutate(ASSIGN_SGW_APPROVALS_MUTATION, { input });
}

/**
 * Update an SGW approval record via API.
 */
export async function updateSgwApprovalApi(input) {
  return walletMutate(UPDATE_SGW_APPROVAL_MUTATION, { input });
}

/**
 * Soft-delete an SGW approval record via API.
 */
export async function softDeleteSgwApprovalApi(id, deletedBy) {
  return walletMutate(SOFT_DELETE_SGW_APPROVAL_MUTATION, { id, deletedBy });
}

